import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronDown, ChevronUp, ClipboardList, Loader2, MapPin, Save, History, AlertCircle } from 'lucide-react';
import { crmStore, nextPrestId, nextPrestIdForClient, tachesStore, useCrm } from '../store';
import type { Prestation, CanalContact, Priorite } from '../types';
import { STATUTS, CANAUX_CONTACT, PRIORITES } from '../types';
import { toast } from 'sonner';
import { searchAddress, reverseGeocode, routeFromAgency, formatDuree, type NominatimSuggestion } from '../utils/geo';
import SearchableMultiSelect from './SearchableMultiSelect';
import ClientPicker from './ClientPicker';
import ArtistesProches from './ArtistesProches';
import BrouillonsLies, { type BrouillonImport } from './BrouillonsLies';
import type { Coords } from '../utils/geo';
import { formatDateFR } from '../utils';
import { STRUCTURE_LABELS, type StructureCode } from '../structureContext';
import IntervenantsPanel from './IntervenantsPanel';
import { normalizeAdresse } from '@/crm/utils/adresse';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial?: Prestation | null;
}

const EMPTY: Prestation = {
  id: '',
  structure: 'cygnature',
  client: '',
  tel: '',
  email: '',
  statut: 'Brouillon',
  dateEvent: '',
  lieu: '',
  km: null,
  duree: '0H30',
  libelle: '',
  demande: '',
  services: [],
  domaines: [],
  themes: [],
  artistes: [],
  propTTC: null,
  propNote: '',
  devis: null,
  acomptePct: 30,
  contratClient: '',
  contratArtiste: '',
  remarques: '',
  factureEmise: '',
  paye: '',
  gcalId: null,
  dateCreation: '',
  archivedAt: null,
  canal: '',
  priorite: '',
};

const LIBELLES = ['Mariage', 'Anniversaire', 'Entreprise', 'Cérémonie', 'Festival', 'Privé', 'Institutionnel', 'Autre'];
const DEMANDES = ['Site Web', 'Téléphone', 'Email', 'Recommandation', 'Réseaux sociaux', 'Salon', 'Autre'];

export default function PrestationForm({ open, onOpenChange, initial }: Props) {
  const data = useCrm();
  const [form, setForm] = useState<Prestation>(EMPTY);
  const [lieuCoords, setLieuCoords] = useState<Coords | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...initial } : { ...EMPTY, dateCreation: new Date().toISOString().slice(0, 10) });
    }
  }, [open, initial]);

  const set = <K extends keyof Prestation>(k: K, v: Prestation[K]) => setForm((f) => ({ ...f, [k]: v }));

  const toggleMulti = (key: 'services' | 'domaines' | 'artistes' | 'themes', value: string) => {
    setForm((f) => {
      const arr = (f as any)[key] as string[];
      return { ...f, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
    });
  };

  const handleImportBrouillon = (b: BrouillonImport) => {
    setForm((f) => {
      const next = { ...f };
      if (b.prop_ttc != null) next.propTTC = b.prop_ttc;
      if (b.km != null) next.km = b.km;
      if (b.duree_trajet) next.duree = b.duree_trajet;
      if (b.domaine && !f.domaines.includes(b.domaine)) next.domaines = [...f.domaines, b.domaine];
      if (b.artiste_nom && !f.artistes.includes(b.artiste_nom)) next.artistes = [...f.artistes, b.artiste_nom];
      // Construit une note récapitulative
      const noteParts: string[] = [];
      if (b.artiste_nom) noteParts.push(b.artiste_nom);
      if (b.tarif_ht != null) noteParts.push(`HT ${b.tarif_ht}€`);
      if (b.frais != null && b.frais > 0) noteParts.push(`frais ${b.frais}€`);
      if (b.marge_pct != null) noteParts.push(`marge ${b.marge_pct}%`);
      const note = noteParts.join(' · ');
      next.propNote = f.propNote ? `${f.propNote}\n${note}` : note;
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.client.trim()) return toast.error('Le nom du client est obligatoire');
    if (!form.dateEvent) return toast.error('La date événement est obligatoire');
    const toSave: Prestation = {
      ...form,
      id: form.id || nextPrestIdForClient(form.client, form.dateEvent),
      dateCreation: form.dateCreation || new Date().toISOString().slice(0, 10),
    };
    await crmStore.upsertPrest(toSave);

    // Application auto du template de tâches selon le statut
    try {
      const added = await tachesStore.applyTemplate(toSave.id, toSave.statut);
      if (added > 0) toast.message(`${added} tâche(s) ajoutée(s) à la checklist`);
    } catch (err) { console.error('[taches template]', err); }

    // Création auto des relances quand le statut bascule à "Propal transmise"
    const wasNotPropal = !initial || initial.statut !== 'Propal transmise';
    if (toSave.statut === 'Propal transmise' && wasNotPropal) {
      try {
        const created = await crmStore.scheduleRelancesPropal(toSave.id);
        if (created > 0) toast.success(`${created} relance(s) planifiée(s) (J+3, J+7, J+15, J+30)`);
      } catch (err) {
        console.error('[relances auto]', err);
      }
    }

    toast.success(initial ? 'Prestation mise à jour' : 'Nouvelle prestation créée');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading text-slate-100 flex items-center gap-3">
            <ClipboardList size={22} strokeWidth={1.5} className="text-amber-400" />
            {initial ? 'Modifier Prestation' : 'Nouvelle Prestation'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Structure émettrice *" className="md:col-span-2">
              <div className="flex gap-2">
                {(['cygnature', 'suivez_vos_reves'] as StructureCode[]).map((s) => {
                  const active = form.structure === s;
                  const cls = s === 'cygnature'
                    ? (active ? 'bg-amber-500/20 border-amber-500/50 text-amber-200' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-amber-500/30')
                    : (active ? 'bg-violet-500/20 border-violet-500/50 text-violet-200' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-violet-500/30');
                  return (
                    <button key={s} type="button" onClick={() => set('structure', s)}
                      className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${cls}`}>
                      {STRUCTURE_LABELS[s]}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="Client *">
              <ClientPicker
                value={form.client}
                onChange={(v) => set('client', v)}
                clients={data.clients}
                onPickClient={(c) => {
                  setForm((f) => ({
                    ...f,
                    client: [c.prenom, c.nom].filter(Boolean).join(' ').trim() || c.societe || f.client,
                    tel: c.tel || f.tel,
                    email: c.email || f.email,
                    lieu: f.lieu || normalizeAdresse(c.adresse, c.cp, c.ville, ' '),
                  }));
                  toast.success(`Client « ${c.societe || c.nom} » chargé`);
                }}
              />
            </Field>
            <Field label="Date événement *">
              <input required type="date" value={form.dateEvent} onChange={(e) => set('dateEvent', e.target.value)} className={inputCls} />
            </Field>

            <Field label="Lieu du projet" className="md:col-span-2">
              <LieuAutocomplete
                value={form.lieu}
                onChange={(v) => set('lieu', v)}
                onSelect={(addr, km, dureeStr, coords) => {
                  set('lieu', addr);
                  if (km != null) set('km', km);
                  if (dureeStr) set('duree', dureeStr);
                  if (coords) setLieuCoords(coords);
                }}
              />
            </Field>

            <Field label="KM (depuis l'agence)">
              <input type="number" value={form.km ?? ''} onChange={(e) => set('km', e.target.value ? Number(e.target.value) : null)} placeholder="km" className={inputCls} />
            </Field>
            <Field label="Durée trajet (auto)">
              <input value={form.duree} onChange={(e) => set('duree', e.target.value)} placeholder="0h30" className={inputCls} />
            </Field>

            <Field label="Statut">
              <select value={form.statut} onChange={(e) => set('statut', e.target.value)} className={inputCls}>
                {STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Priorité">
              <select value={form.priorite || ''} onChange={(e) => set('priorite', e.target.value as Priorite)} className={inputCls}>
                <option value="">—</option>
                {PRIORITES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Canal de contact">
              <select value={form.canal || ''} onChange={(e) => set('canal', e.target.value as CanalContact)} className={inputCls}>
                <option value="">—</option>
                {CANAUX_CONTACT.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Source / Demande">
              <select value={form.demande} onChange={(e) => set('demande', e.target.value)} className={inputCls}>
                <option value="">—</option>
                {DEMANDES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>

            <Field label="Libellé (type événement)">
              <select value={form.libelle} onChange={(e) => set('libelle', e.target.value)} className={inputCls}>
                <option value="">—</option>
                {LIBELLES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Téléphone">
              <input type="tel" value={form.tel} onChange={(e) => set('tel', e.target.value)} className={inputCls} />
            </Field>

            <Field label="Email" className="md:col-span-2">
              <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} />
            </Field>

            <SearchableMultiSelect label="Services" options={data.services} selected={form.services} onToggle={(v) => toggleMulti('services', v)} className="md:col-span-2" placeholder="Tape pour rechercher (ex : 'sax')…" />
            <SearchableMultiSelect label="Domaines artistiques" options={data.domaines} selected={form.domaines} onToggle={(v) => toggleMulti('domaines', v)} className="md:col-span-2" placeholder="Tape pour rechercher un domaine…" />
            <SearchableMultiSelect label="Type d'événement" options={data.themes || []} selected={form.themes || []} onToggle={(v) => toggleMulti('themes', v)} className="md:col-span-2" placeholder="Tape pour rechercher un type d'événement (ex : Arbre de Noël)…" />
            <SearchableMultiSelect label="Artistes" options={data.artistes} selected={form.artistes} onToggle={(v) => toggleMulti('artistes', v)} className="md:col-span-2" placeholder="Tape pour rechercher un artiste…" />

            <div className="md:col-span-2">
              <ArtistesProches
                lieuCoords={lieuCoords}
                lieuLabel={form.lieu}
                domaines={form.domaines}
                selected={form.artistes}
                onToggle={(nom) => toggleMulti('artistes', nom)}
              />
            </div>

            <div className="md:col-span-2">
              <BrouillonsLies prestation={form} onImport={handleImportBrouillon} />
            </div>

            <Field label="Proposition TTC (€)">
              <input type="number" step="0.01" value={form.propTTC ?? ''} onChange={(e) => set('propTTC', e.target.value ? Number(e.target.value) : null)} className={inputCls} />
            </Field>
            <Field label="Note prop. / devis">
              <input value={form.propNote} onChange={(e) => set('propNote', e.target.value)} placeholder="Ex: WP 2000€, DJ 1750€…" className={inputCls} />
            </Field>

            <Field label="Acompte (%)">
              <input type="number" value={form.acomptePct ?? ''} onChange={(e) => set('acomptePct', e.target.value ? Number(e.target.value) : null)} className={inputCls} />
            </Field>
            <Field label="Échéance paiement">
              <input type="date" value={form.factureEmise} onChange={(e) => set('factureEmise', e.target.value)} className={inputCls} />
            </Field>

            <Field label="Contrat client">
              <select value={form.contratClient} onChange={(e) => set('contratClient', e.target.value)} className={inputCls}>
                <option value="">—</option>
                <option value="Brouillon">Brouillon</option>
                <option value="En attente">En attente</option>
                <option value="Signé">Signé</option>
                <option value="Refusé">Refusé</option>
              </select>
            </Field>
            <Field label="Contrat artiste">
              <select value={form.contratArtiste} onChange={(e) => set('contratArtiste', e.target.value)} className={inputCls}>
                <option value="">—</option>
                <option value="Brouillon">Brouillon</option>
                <option value="En attente">En attente</option>
                <option value="Signé">Signé</option>
                <option value="Refusé">Refusé</option>
              </select>
            </Field>

            <Field label="Facture émise ?">
              <select value={form.factureEmise} onChange={(e) => set('factureEmise', e.target.value)} className={inputCls}>
                <option value="">—</option>
                <option value="Oui">Oui</option>
                <option value="Non">Non</option>
              </select>
            </Field>
            <Field label="Payé ?">
              <select value={form.paye} onChange={(e) => set('paye', e.target.value)} className={inputCls}>
                <option value="">—</option>
                <option value="Oui">Oui</option>
                <option value="Partiel">Partiel</option>
                <option value="Non">Non</option>
              </select>
            </Field>

            <Field label="Remarques" className="md:col-span-2">
              <textarea value={form.remarques} onChange={(e) => set('remarques', e.target.value)} rows={3} className={inputCls} />
            </Field>
          </div>

          {initial && <IntervenantsPanel prestNumero={initial.id} />}
          {initial && <ChecklistTaches prestId={initial.id} statut={form.statut} />}
          {initial && <PrestationHistorique prestNumero={initial.id} dateCreation={initial.dateCreation} />}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
            <button type="button" onClick={() => onOpenChange(false)} className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm text-slate-200 transition-colors">
              Annuler
            </button>
            <button id="prestation-save-btn" type="submit" className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-medium rounded-lg text-sm flex items-center gap-2 transition-colors">
              <Save size={14} strokeWidth={1.5} /> Enregistrer
            </button>
          </div>
        </form>

        {/* Flèche flottante pour aller directement au bouton Enregistrer */}
        <button
          type="button"
          aria-label="Aller au bouton Enregistrer"
          title="Aller au bouton Enregistrer"
          onClick={() => {
            const el = document.getElementById('prestation-save-btn');
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
          className="sticky bottom-4 float-right -mt-10 mr-1 z-10 w-10 h-10 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-lg flex items-center justify-center transition-colors"
        >
          <ChevronDown size={18} strokeWidth={2} />
        </button>
      </DialogContent>
    </Dialog>
  );
}

const inputCls = 'w-full px-3 py-2.5 bg-slate-950/60 border border-slate-800 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-500/50';

function Field({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label className="block text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function MultiSelect(_: any) { return null; }

// ============ Checklist Tâches d'une prestation ============
function ChecklistTaches({ prestId, statut }: { prestId: string; statut: string }) {
  const [taches, setTaches] = useState<any[]>([]);
  const [nouveau, setNouveau] = useState('');
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    const list = await tachesStore.list(prestId);
    setTaches(list);
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [prestId]);

  const totalDone = taches.filter((t) => t.fait).length;
  const pct = taches.length ? Math.round((totalDone / taches.length) * 100) : 0;

  const handleAdd = async () => {
    if (!nouveau.trim()) return;
    await tachesStore.add(prestId, nouveau.trim(), { ordre: taches.length });
    setNouveau('');
    reload();
  };

  const handleApplyTemplate = async () => {
    const added = await tachesStore.applyTemplate(prestId, statut);
    if (added > 0) toast.success(`${added} tâche(s) ajoutée(s) depuis le template "${statut}"`);
    else toast.message('Aucune tâche à ajouter (déjà présentes ou template vide)');
    reload();
  };

  return (
    <div className="border border-slate-800 rounded-lg p-4 bg-slate-950/40 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
          ✅ Checklist ({totalDone}/{taches.length}) — {pct}%
        </div>
        <button type="button" onClick={handleApplyTemplate}
          className="text-xs px-2.5 py-1.5 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-300 rounded">
          + Template "{statut}"
        </button>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mb-3">
        <div className="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} />
      </div>
      {loading ? <div className="text-xs text-slate-500">Chargement…</div> : (
        <ul className="space-y-1.5 mb-3">
          {taches.map((t) => (
            <li key={t.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={t.fait} onChange={(e) => { tachesStore.toggle(t.id, e.target.checked).then(reload); }} className="accent-amber-500" />
              <span className={`flex-1 ${t.fait ? 'line-through text-slate-500' : 'text-slate-200'}`}>
                {t.libelle}{t.categorie && <span className="text-slate-500 text-xs ml-2">[{t.categorie}]</span>}
              </span>
              <button type="button" onClick={() => tachesStore.remove(t.id).then(reload)} className="text-red-400/70 hover:text-red-400 text-xs">✕</button>
            </li>
          ))}
          {!taches.length && <li className="text-xs text-slate-500 italic">Aucune tâche. Utilise le template ou ajoute manuellement.</li>}
        </ul>
      )}
      <div className="flex gap-2">
        <input value={nouveau} onChange={(e) => setNouveau(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          placeholder="Nouvelle tâche…"
          className="flex-1 px-3 py-1.5 bg-slate-900 border border-slate-800 rounded text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50" />
        <button type="button" onClick={handleAdd} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded text-xs text-slate-200">+ Ajouter</button>
      </div>
    </div>
  );
}

// ============ Historique d'activité d'une prestation ============
function PrestationHistorique({ prestNumero, dateCreation }: { prestNumero: string; dateCreation: string }) {
  const [entries, setEntries] = useState<Array<{ id: string; created_at: string; action: string; user_email: string | null; old_data: any; new_data: any }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    crmStore.fetchPrestationHistory(prestNumero).then((rows) => {
      if (!cancelled) { setEntries(rows); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [prestNumero]);

  const lastDate = entries[0]?.created_at || dateCreation;
  const daysSince = useMemo(() => {
    if (!lastDate) return null;
    const ms = Date.now() - new Date(lastDate).getTime();
    return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
  }, [lastDate]);

  const inactivityColor =
    daysSince == null ? 'text-slate-400' :
    daysSince <= 7 ? 'text-emerald-400' :
    daysSince <= 15 ? 'text-amber-400' :
    'text-rose-400';

  const labelAction = (action: string, newData: any, oldData: any) => {
    if (action === 'INSERT') return 'Création du dossier';
    if (action === 'DELETE') return 'Suppression';
    if (action === 'UPDATE') {
      if (oldData?.statut && newData?.statut && oldData.statut !== newData.statut) {
        return `Statut : ${oldData.statut} → ${newData.statut}`;
      }
      if (oldData?.archived_at !== newData?.archived_at) {
        return newData?.archived_at ? 'Archivée' : 'Désarchivée';
      }
      return 'Modification';
    }
    return action;
  };

  return (
    <div className="border border-slate-800 rounded-lg bg-slate-950/40 p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
          <History size={14} strokeWidth={1.5} className="text-amber-400" />
          Historique d'activité
        </div>
        {daysSince != null && (
          <div className={`flex items-center gap-1.5 text-xs ${inactivityColor}`}>
            <AlertCircle size={12} strokeWidth={1.5} />
            <span>
              Dernière action il y a <strong>{daysSince}j</strong>
              {daysSince > 15 && ' · dossier inactif'}
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-slate-500 italic">Chargement…</div>
      ) : entries.length === 0 ? (
        <div className="text-xs text-slate-500 italic">Aucun historique disponible.</div>
      ) : (
        <HistoriqueList entries={entries} labelAction={labelAction} />
      )}
    </div>
  );
}

function HistoriqueList({
  entries,
  labelAction,
}: {
  entries: Array<{ id: string; created_at: string; action: string; user_email: string | null; old_data: any; new_data: any }>;
  labelAction: (action: string, newData: any, oldData: any) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? entries : entries.slice(0, 3);
  const hidden = entries.length - visible.length;
  return (
    <>
      <ol className="relative border-l border-slate-800 ml-2 space-y-2.5">
        {visible.map((e) => (
          <li key={e.id} className="ml-3 pl-3 relative">
            <span className="absolute -left-[7px] top-1.5 w-2 h-2 rounded-full bg-amber-400" />
            <div className="text-xs text-slate-300">{labelAction(e.action, e.new_data, e.old_data)}</div>
            <div className="text-[10px] text-slate-500">
              {formatDateFR(e.created_at, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
              {e.user_email && ` · ${e.user_email}`}
            </div>
          </li>
        ))}
      </ol>
      {entries.length > 3 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200"
        >
          {expanded ? (
            <>
              <ChevronUp size={12} strokeWidth={1.5} /> Réduire
            </>
          ) : (
            <>
              <ChevronDown size={12} strokeWidth={1.5} /> Voir les {hidden} entrées plus anciennes
            </>
          )}
        </button>
      )}
    </>
  );
}


function LieuAutocomplete({
  value,
  onChange,
  onSelect,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect: (address: string, km: number | null, dureeStr: string | null, coords: Coords | null) => void;
}) {
  const [suggestions, setSuggestions] = useState<NominatimSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);
  const [locating, setLocating] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = (q: string) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (q.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchAddress(q);
        setSuggestions(data);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const handleChange = (v: string) => {
    onChange(v);
    fetchSuggestions(v);
  };

  const computeAndApply = async (address: string, lat: number, lon: number) => {
    setComputing(true);
    try {
      const route = await routeFromAgency({ lat, lon });
      onSelect(address, route.km, formatDuree(route.minutes), { lat, lon });
      if (route.source === 'haversine') {
        toast.info('Trajet routier indisponible — distance approximative');
      }
    } catch {
      onSelect(address, null, null, { lat, lon });
      toast.error('Impossible de calculer le trajet');
    } finally {
      setComputing(false);
    }
  };

  const handlePick = (s: NominatimSuggestion) => {
    setOpen(false);
    setSuggestions([]);
    void computeAndApply(s.display_name, parseFloat(s.lat), parseFloat(s.lon));
  };

  const handleLocate = () => {
    if (!navigator.geolocation) {
      toast.error('Géolocalisation non disponible sur ce navigateur');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const addr = await reverseGeocode(latitude, longitude);
          await computeAndApply(addr, latitude, longitude);
          toast.success('Position détectée');
        } catch {
          toast.error("Impossible de récupérer l'adresse");
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === 1 ? 'Autorisation refusée' : 'Localisation impossible');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const busy = loading || computing;

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={() => suggestions.length && setOpen(true)}
            placeholder="Ville, commune, lieu…"
            className={inputCls}
            autoComplete="off"
          />
          {busy && (
            <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 animate-spin" />
          )}
        </div>
        <button
          type="button"
          onClick={handleLocate}
          disabled={locating || computing}
          className="px-3 bg-slate-800 hover:bg-slate-700 rounded-lg text-amber-400 disabled:opacity-50"
          title="Utiliser ma position"
        >
          {locating ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} strokeWidth={1.5} />}
        </button>
      </div>
      {computing && (
        <div className="mt-1 text-[10px] text-amber-400/80 italic">Calcul du trajet routier en cours…</div>
      )}
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-slate-950 border border-slate-800 rounded-lg shadow-xl">
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onClick={() => handlePick(s)}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/80 border-b border-slate-800/50 last:border-0 flex items-start gap-2"
            >
              <MapPin size={12} className="text-amber-400 mt-1 shrink-0" strokeWidth={1.5} />
              <span className="line-clamp-2">{s.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
